import { useEffect, useMemo, useState } from "react";
import {
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  chakra,
  Spinner,
  Text,
  TabProps,
  Button,
} from "@chakra-ui/react";

// local component
import ClaimItem from "./ClaimItem";
import ClaimItemHeader from "./ClaimItemHeader";

// common components
import { ModalWrapper, TransactionResult, TxStatusModalBody, Alert } from "ui";

// hooks
import { useHashConnect, useTransactionFee, useWithdrawals } from "hooks";

// third party
import { delay } from "lodash";
import { transactionResultMessage } from "utils/transactionResultMessage";

// config
import { config } from "config/config";

// firebase analytics
import { firebaseEvents } from "utils/firebase";
import { FIREBASE_EVENT_PROPERTIES } from "constants/constants";

const ClaimContent = chakra(Flex, {
  baseStyle: {
    fontSize: 14,
    mt: 2,
    pr: { base: 0, sm: 2 },
    maxH: { base: "initial", md: 300 },
    minH: 61,
    overflowY: "auto",
    gap: 2,
    flexDir: "column",
  },
});

const tabTitles = ["Active", "Completed"];

const tabProps: TabProps = {
  color: "gray.500",
  _focus: {
    outline: "none",
  },
  _selected: { color: "brand.primary", borderBottomColor: "brand.primary" },
};

const Claim = () => {
  const {
    status,
    showWalletModal,
    transactionStatus,
    withdrawStatus,
    withdrawId,
    selectedAccount,
    setTransactionId,
    walletData,
  } = useHashConnect();
  const oldContractIds = {
    stakingContractId: config.ids.oldStakingContractId,
    undelegationContractId: config.ids.oldUndelegationContractId,
  };
  const contractIds = {
    stakingContractId: config.ids.stakingContractId,
    undelegationContractId: config.ids.undelegationContractId,
  };

  const { data: oldData, getUnStakeData: getOldUnStakeData } =
    useWithdrawals(oldContractIds);
  const { data, getUnStakeData } = useWithdrawals(contractIds);
  const [transactionModal, showTransactionModal] = useState<boolean>(false);
  const [transactionResultModal, showTransactionResultModal] =
    useState<boolean>(false);
  const [transactionResult, setTransactionResult] = useState<string>("");
  // const [transactionResultId, setTransactionResultId] = useState<string>("");
  const [initalFetching, setInitalFetching] = useState<boolean>(true);
  const { fee: transactionFee } = useTransactionFee();
  const sendLog = (name: string) => {
    let firebaseRequest: any = {
      [FIREBASE_EVENT_PROPERTIES.EVENT_NAME]: name,
      [FIREBASE_EVENT_PROPERTIES.WALLET_NAME]:
        walletData.pairedWalletData?.name,
      [FIREBASE_EVENT_PROPERTIES.WALLET_ADDRESS]: selectedAccount,
      // [FIREBASE_EVENT_PROPERTIES.TOKEN_BALANCE]: balances.token.balance,
      // [FIREBASE_EVENT_PROPERTIES.LIQUIDTOKEN_BALANCE]: balances.xtoken.balance,
    };
    firebaseEvents(firebaseRequest);
  };
  const fetchData = async () => {
    await getUnStakeData();
    await getOldUnStakeData();
    setInitalFetching(false);
  };

  useEffect(() => {
    delay(fetchData, 3000);
  }, [transactionStatus, withdrawStatus]);

  useEffect(() => {
    if (selectedAccount !== "") {
      setInitalFetching(true);
      fetchData();
    }
  }, [selectedAccount]);

  useEffect(() => {
    if (withdrawStatus === "IN_PROGRESS" && withdrawId) {
      // trackEvent(TRANSACTION_START, { transaction_type: "withdrawl" });
      sendLog("Withdraw_cta");
      !transactionModal && showTransactionModal(true);
    } else {
      transactionModal && showTransactionModal(false);
    }
  }, [withdrawStatus, withdrawId]);

  // const handleTxView = (txId: string) => {
  //   const url = getTransactionURL(txId as string);
  //   window.open(url, "_blank");
  // };

  const claimedTransactions = useMemo(() => {
    const newContractData = data
      .filter((s) => s.isWithDrawn)
      .sort((a, b) => b.time - a.time);
    const oldContractData = oldData
      .filter((s) => s.isWithDrawn)
      .sort((a, b) => b.time - a.time);
    return [...newContractData, ...oldContractData];
  }, [data, oldData]);

  const unClaimedTransactions = useMemo(() => {
    const newContractData = data
      .map((s) => {
        return { ...s, contractType: "new" };
      })
      .filter((s) => !s.isWithDrawn)
      .sort((a, b) => b.time - a.time);
    const oldContractData = oldData
      .map((s) => {
        return { ...s, contractType: "old" };
      })
      .filter((s) => !s.isWithDrawn)
      .sort((a, b) => b.time - a.time);
    return [...newContractData, ...oldContractData];
  }, [data, oldData]);

  const closeTransactionResultModal = () => {
    showTransactionResultModal(false);
    setTransactionResult("");
    // setTransactionResultId("");
  };

  return (
    <>
      <Alert
        type="warning"
        message={`You will be able to withdraw your rewards after the unstake request
        has been processed. To unstake your amount go to unstake tab`}
        showMessage={!initalFetching}
      />

      <Flex
        width="100%"
        height="188px"
        display={initalFetching ? "flex" : "none"}
        justifyContent="center"
        alignItems="center"
      >
        <Text fontSize="20px" fontWeight="700">
          <Spinner size="lg" verticalAlign="middle" />
        </Text>
      </Flex>

      <Flex
        width="100%"
        height="188px"
        display={
          unClaimedTransactions?.length === 0 &&
          claimedTransactions?.length === 0 &&
          !initalFetching
            ? "flex"
            : "none"
        }
        justifyContent="center"
        alignItems="center"
      >
        {status === "WALLET_NOT_CONNECTED" ? (
          <Flex gap="2" mt="4">
            <Text color="gray.500">To view your transactions</Text>
            <Button
              variant="link"
              colorScheme="brand.primary"
              onClick={() => {
                sendLog("Connect_Wallet");
                showWalletModal(true);
              }}
            >
              Connect wallet
            </Button>
          </Flex>
        ) : (
          <Flex gap="2" mt="4">
            <Text fontSize="20px" fontWeight="700">
              You don’t have any claims yet!
            </Text>
          </Flex>
        )}
      </Flex>

      <Tabs
        isLazy
        mt="2rem"
        display={
          (unClaimedTransactions.length > 0 ||
            claimedTransactions.length > 0) &&
          !initalFetching
            ? "block"
            : "none"
        }
      >
        <TabList>
          {tabTitles.map((t) => (
            <Tab {...tabProps} key={t} color="brand.primary" mr="8px">
              {t}
            </Tab>
          ))}
        </TabList>

        <TabPanels>
          <TabPanel p="0">
            {unClaimedTransactions && unClaimedTransactions.length > 0 && (
              <ClaimItemHeader
                section="active"
                items={unClaimedTransactions.length}
              ></ClaimItemHeader>
            )}
            <ClaimContent className="visible-scrollbar">
              {unClaimedTransactions && unClaimedTransactions.length > 0 ? (
                unClaimedTransactions.map((tx) => (
                  <ClaimItem
                    transaction={tx}
                    key={tx.index}
                    setTransactionResult={setTransactionResult}
                    // setTransactionResultId={setTransactionResultId}
                    showTransactionResultModal={showTransactionResultModal}
                    transactionFee={transactionFee}
                    selectedAccount={selectedAccount}
                  />
                ))
              ) : (
                <Flex align="center" justify="center">
                  {status === "INITIALIZING" ? (
                    <Spinner mt="7" />
                  ) : status === "WALLET_NOT_CONNECTED" ? (
                    <Flex gap="2" mt="4">
                      <Text color="gray.500">To view your transactions</Text>
                      <Button
                        variant="link"
                        colorScheme="brand.primary"
                        onClick={() => showWalletModal(true)}
                      >
                        Connect wallet
                      </Button>
                    </Flex>
                  ) : (
                    <Flex
                      mt="7px"
                      width="100%"
                      height="188px"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Text fontSize="20px" fontWeight="700">
                        To withdraw your HBAR, please unstake first
                      </Text>
                    </Flex>
                  )}
                </Flex>
              )}
            </ClaimContent>
          </TabPanel>
          <TabPanel p="0">
            {claimedTransactions && claimedTransactions.length > 0 && (
              <ClaimItemHeader
                section="completed"
                items={claimedTransactions.length}
              ></ClaimItemHeader>
            )}
            <ClaimContent className="visible-scrollbar">
              {claimedTransactions && claimedTransactions.length > 0 ? (
                claimedTransactions.map((tx) => (
                  <ClaimItem transaction={tx} key={tx.index} />
                ))
              ) : (
                <Flex align="center" justify="center">
                  {status === "INITIALIZING" ? (
                    <Spinner mt="7" />
                  ) : status === "WALLET_NOT_CONNECTED" ? (
                    <Flex gap="2" mt="4">
                      <Text color="gray.500">To view your transactions</Text>
                      <Button
                        variant="link"
                        colorScheme="brand.primary"
                        color="brand.textWhite"
                        onClick={() => showWalletModal(true)}
                      >
                        Connect wallet
                      </Button>
                    </Flex>
                  ) : (
                    <Flex
                      mt="7px"
                      width="100%"
                      height="188px"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Text fontSize="20px" fontWeight="700">
                        You have not made any withdrawls yet
                      </Text>
                    </Flex>
                  )}
                </Flex>
              )}
            </ClaimContent>
          </TabPanel>
        </TabPanels>
      </Tabs>
      <ModalWrapper
        onClose={() => {
          showTransactionModal(false);
          setTransactionId(null);
        }}
        modal={transactionModal}
        body={<TxStatusModalBody transactionWebsite="Hashscan" />}
        header=""
        styles={{ height: "340px" }}
      />

      <ModalWrapper
        onClose={closeTransactionResultModal}
        modal={transactionResultModal}
        body={
          <TransactionResult
            status={transactionResult}
            // openTransactionURL={() => handleTxView(transactionResultId)}
            closeTransactionModal={closeTransactionResultModal}
            message={transactionResultMessage({
              status: transactionResult,
              coin: config.coinName,
              transactionType: "withdraw",
              account: selectedAccount,
            })}
          />
        }
        header=""
        styles={{ height: "340px" }}
      />
    </>
  );
};

export default Claim;
